import React, { useEffect, useMemo, useRef, useState } from "react";
import client from "graphql/client";
import { loader } from "graphql.macro";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { bookEdit } from "redux/actions/books/bookEdit";
import fileAdd from "redux/actions/files/fileAdd";
import Button from "shared/components/buttons/Button";
import Icon from "shared/components/Icon";
import { ICONS } from "shared/components/Icon";
import InputMultiSelect from "shared/components/inputs/InputMultiSelect";
import InputText from "shared/components/inputs/InputText";
import getImageUrl from "shared/helpers/getImageUrl";

import "./style.scss";

const fetchCompanyLabelsQuery = loader("graphql/queries/books/adminCompanyLabels.graphql");

const General = () => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { osbn } = useParams();
    const fileRef = useRef(null);
    const { control, handleSubmit, errors } = useForm();

    const { books } = useSelector(state => state.books);
    const { profile } = useSelector(state => state.user);
    const { current: organisation } = useSelector(state => state.organisation);

    const book = books[osbn];
    const [coverFile, setCoverFile] = useState(null);
    const [coverImage, setCoverImage] = useState(book?.cover);
    const [companyLabelItems, setCompanyLabelItems] = useState([]);

    const loadImage = async () => {
        if (book?.cover) {
            if (!book.cover.includes("https")) {
                const imageUrl = await getImageUrl(book.cover, organisation.id);
                setCoverImage(imageUrl);
            }
        }
    };

    const loadCompanyLabels = async () => {
        try {
            const { data } = await client.query({
                query: fetchCompanyLabelsQuery,
                fetchPolicy: "network-only"
            });

            const labels = (data?.adminCompanyLabels || []).map(({ id, name }) => ({
                value: id.toString(),
                label: name
            }));
            setCompanyLabelItems(labels);
        } catch (error) {
            console.error(error);
            setCompanyLabelItems([]);
        }
    };

    const selectedCompanyLabels = useMemo(() => {
        const labels = book?.companyLabels || book?.labels || [];
        if (!Array.isArray(labels)) {
            return [];
        }

        return labels.map((label) => {
            if (typeof label === "string") {
                return label;
            }
            if (typeof label === "number") {
                return label.toString();
            }
            if (typeof label === "object" && label?.id) {
                return label.id.toString();
            }
            return null;
        }).filter(Boolean);
    }, [book]);

    useEffect(() => {
        loadImage();
        loadCompanyLabels();
    }, []);

    const onFileSelected = ({ target }) => {
        const files = target.files;
        setCoverFile(files[0]);
        setCoverImage(URL.createObjectURL(files[0]));
    };


    const updateBook = async ({ label, author, description, price, companyLabels, moreInformationUrl }) => {
        let filename = null;
        if (coverFile) {
            filename = await dispatch(fileAdd(coverFile));
        }
        const floatPrice = parseFloat(price);

        dispatch(bookEdit(
            osbn,
            label,
            author,
            description,
            floatPrice,
            filename,
            companyLabels,
            moreInformationUrl
        ));
    };

    const selectFile = () => {
        fileRef.current.click();
    };

    const renderCover = () => {
        return (
            <div className="cover">
                <input
                    className="hidden-file"
                    type="file"
                    onChange={onFileSelected}
                    ref={fileRef}
                />
                <img src={coverImage || require("assets/img/books-placeholder.png")} />
                <div className="image-change">
                    <Icon
                        name={ICONS.CAMERA}
                        onClick={selectFile}
                    />
                </div>
                <div className="ratio">
                    5:7
                </div>
            </div>
        );
    };


    return (
        <div className="section-general">
            <h2>{t("book.edit.basic.title")}</h2>
            <div className="divider" />
            <div className="cols-m">
                {renderCover()}
                <div className="col">
                    <InputText
                        name="label"
                        label={t("book.edit.label")}
                        defaultValue={book.label}
                        control={control}
                        rules={{ required: true }}
                        error={errors.label}
                        dataTestId="label"
                    />
                    <InputText
                        name="author"
                        label={t("book.edit.author")}
                        defaultValue={book.author || `${profile?.name} ${profile?.surname}`}
                        control={control}
                        rules={{ required: true }}
                        error={errors.author}
                        dataTestId="author"
                    />
                    <InputText
                        name="price"
                        defaultValue={book.price}
                        label={t("book.edit.price")}
                        control={control}
                        rules={{ required: true }}
                        type="number"
                    />

                </div>
            </div>
            <InputText
                name="description"
                label={t("book.edit.description")}
                defaultValue={book.description}
                control={control}
                rules={{ required: true }}
                error={errors.description}
                multiline
                dataTestId="description"
            />
            <InputMultiSelect
                name="companyLabels"
                label={t("book.edit.labels")}
                defaultValue={selectedCompanyLabels}
                items={companyLabelItems}
                control={control}
                dataTestId="companyLabels"
            />
            <InputText
                name="moreInformationUrl"
                label={t("book.edit.moreInformationURL")}
                defaultValue={book.moreInformationUrl || book.moreInfoUrl || ""}
                control={control}
                type="url"
                dataTestId="moreInformationUrl"
            />
            <div className="divider" />
            <Button
                label={t("button.save.label")}
                onClick={handleSubmit(updateBook)}
            />
        </div>
    );
};

export default General;
